import React, { createContext, useContext, useState, useCallback } from 'react';

const SESSION_KEY = 'finsuite_impersonation';

interface ImpersonationState {
  alias: string;
  displayName: string;
}

interface ImpersonationContextValue {
  impersonation: ImpersonationState | null;
  setImpersonation: (state: ImpersonationState | null) => void;
  clearImpersonation: () => void;
}

const ImpersonationContext = createContext<ImpersonationContextValue>({
  impersonation: null,
  setImpersonation: () => {},
  clearImpersonation: () => {}
});

export const useImpersonation = () => useContext(ImpersonationContext);

const loadFromSession = (): ImpersonationState | null => {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export const ImpersonationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [impersonation, setImpersonationState] = useState<ImpersonationState | null>(loadFromSession);

  const setImpersonation = useCallback((state: ImpersonationState | null) => {
    setImpersonationState(state);
    if (state) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
  }, []);

  const clearImpersonation = useCallback(() => setImpersonation(null), [setImpersonation]);

  return <ImpersonationContext.Provider value={{ impersonation, setImpersonation, clearImpersonation }}>{children}</ImpersonationContext.Provider>;
};
